<template>
  <div class="candidate application-phase-stepper">
    <div
      class="pb-3"
      v-if="application.status !== 'Withdrawn' && application.status !== 'Unsuccessful'"
    >
      <ol class="phases-stepper" :class="stepperClass" :style="stepperColors">
        <li
          v-for="(phase, i) in application.opportunity.phases"
          :key="`phase-${i}`"
          :class="{
            'mdi current': i === currentPhaseIndex,
            active: i === currentPhaseIndex,
            visible: i === 0,
          }"
        >
          <span v-if="i === 0"> Applied </span>
          <span
            v-else-if="i === application.opportunity.phases.length - 1"
            :class="[
              phase.name === application.status ? '' : themes.candidate.light.mutedText,
              'visible',
            ]"
          >
            Success!
          </span>
          <span
            v-if="phase.event && phase.event.createdAt"
            class="subscript"
            :class="themes.candidate.light.mutedText"
          >
            {{ new Date(phase.event.createdAt) | date('d MMM yy') }}
          </span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
// Helpers
import _ from 'lodash'

// Mixins
import { colorStringToHex } from '@/mixins/colorStringToHex.js'

export default {
  mixins: [colorStringToHex],

  props: { application: { type: Object, required: true } },

  computed: {
    currentPhaseIndex() {
      if ( !_.get(this, 'application.opportunity.phases[0]')) return

      return this.application.opportunity.phases.findIndex(p => p.name === this.application.status)
    },

    stepperClass() {
      if (this.application.status === 'Withdrawn' || this.application.status === 'Unsuccessful') {
        return { 'phases-stepper--error': true }
      }

      if (!this.application.complete) return { 'phases-stepper--warning': true }
    },

    stepperColor() {
      if (this.application.status === 'Withdrawn' || this.application.status === 'Unsuccessful') {
        return this.themes.candidate.light.error
      }

      if (!this.application.complete) return this.themes.candidate.light.warning

      return this.themes.candidate.light.success
    },

    stepperColors() {
      return {
        '--stepper-white': this.colorStringToHex('white'),
        '--stepper-grey': this.colorStringToHex(this.themes.candidate.light.muted),
        '--stepper-progress': this.colorStringToHex(this.themes.candidate.light.success),
        '--stepper-warning': this.colorStringToHex(this.themes.candidate.light.warning),
        '--stepper-error': this.colorStringToHex(this.themes.candidate.light.error),
      }
    },
  },
}
</script>

<style lang="scss">
.candidate.application-phase-stepper {
  .phases-stepper {
    display: flex;
    justify-content: space-between;
    list-style: none;
    padding: 0;
    margin: 0 0 1rem 0;

    li {
      flex: 2;
      position: relative;
      padding: 0 0 1rem 0;
      line-height: 1.5;
      white-space: nowrap;
      overflow: visible;
      min-width: 0;
      text-align: center;
      color: var(--stepper-progress);
      border-bottom: 5px solid var(--stepper-grey);

      &:first-child {
        text-align: left;
        flex: 1;
      }

      &:last-child {
        text-align: right;
        flex: 1;
      }

      &:before {
        content: '';
        display: block;
        width: 19px;
        height: 19px;
        background-color: var(--stepper-progress);
        border-radius: 50%;
        border: 4px solid var(--stepper-white);
        position: absolute;
        left: calc(50% - 10px);
        bottom: -12px;
        z-index: 3;
        transition: all 0.2s ease-in-out;
      }

      &.current {
        &:before {
          text-align: center;
          content: '\F0004';
          font-size: 18px;
          line-height: 20px;
          left: calc(50% - 17px);
          width: 30px;
          height: 30px;
          bottom: -17px;
        }
      }

      &:first-child:before {
        left: -1px;
      }

      &:last-child:before {
        right: -1px;
        left: auto;
      }

      &:not(.active):not(.visible) span {
        opacity: 0;
      }
      &:not(.active):not(.visible) span.visible {
        opacity: 1;
      }

      &:not(:first-child):after,
      .active:not(:first-child):after {
        content: '';
        display: block;
        width: 100%;
        position: absolute;
        bottom: -5px;
        left: -50%;
        z-index: 2;
        border-bottom: 5px solid var(--stepper-progress);
      }

      &:last-child span {
        width: 200%;
        display: inline-block;
        position: absolute;
        left: -100%;
      }

      &.active:before,
      &.visible:before {
        background-color: var(--stepper-white);
        border-color: var(--stepper-progress);
      }

      &.active ~ li:before {
        border-color: var(--stepper-white);
        background-color: var(--stepper-grey);
      }

      &.active ~ li:after {
        border-color: var(--stepper-grey);
      }

      span.subscript {
        opacity: 1;
        position: absolute;
        width: 100%;
        margin-top: 3.7rem;
        left: 0px;
        font-size: 11px;
      }

      &:first-child > span.subscript {
        text-align: left;
      }

      &:last-child > span.subscript {
        text-align: right;
      }
    }

    span {
      transition: opacity 0.3s ease-in-out;
    }

    .complete:last-child:after,
    .active:last-child:after {
      width: 200%;
      left: -100%;
    }

    .active ~ li {
      border-color: var(--stepper-grey);
    }

    &.phases-stepper--warning {
      li {
        color: var(--stepper-warning);

        &:before {
          background-color: var(--stepper-warning);
        }

        &.active:before,
        &.visible:before {
          background-color: var(--stepper-white);
          border-color: var(--stepper-warning);
        }

        &:not(:first-child):after,
        .active:not(:first-child):after {
          border-bottom: 5px solid var(--stepper-warning);
        }
      }

      .active ~ li:after {
        border-color: var(--stepper-grey);
      }
    }

    &.phases-stepper--error {
      li {
        color: var(--stepper-error);

        &:before {
          background-color: var(--stepper-error);
        }

        &.active:before,
        &.visible:before {
          background-color: var(--stepper-white);
          border-color: var(--stepper-error);
        }

        &:not(:first-child):after,
        .active:not(:first-child):after {
          border-bottom: 5px solid var(--stepper-error);
        }
      }

      .active ~ li:after {
        border-color: var(--stepper-grey);
      }
    }
  }
}
candidate.application-phase-stepper {
}
</style>
